export type CompanyDocumentRevisionStatus = 'IN_USE' | 'IN_PROGRESS' | 'OBSOLETE';

export interface CompanyDocumentUser {
  userId: number;
  firstName: string;
  lastName: string;
  [key: string]: any;
}

export interface CompanyDocumentGroup {
  companyDocumentGroupId: number | null;
  code: string;
  name: string;
}

export interface CompanyDocumentAttachment {
  attachmentId?: number | null;
  fileNameOriginal: string;
  url: string;
  [key: string]: any;
}

export interface CompanyDocument {
  companyDocumentId: number | null;
  code: string;
  department: string;
  name: string;
  companyDocumentGroup: CompanyDocumentGroup | null;
  creatorUser: CompanyDocumentUser | null;
  currentRevisionId: number | null;
  currentRevisionCode: string | null;
  revisionInProgress?: CompanyDocumentRevision | null;
  allSigned?: boolean | null;
  createTime?: string;
  updateTime?: string;
}

export interface CompanyDocumentRevision {
  companyDocumentRevisionId: number | null;
  companyDocumentId: number | null;
  companyDocument?: CompanyDocument | null;
  code: string;
  date: string | null;
  description: string;
  status: CompanyDocumentRevisionStatus;
  reviewedUser: CompanyDocumentUser | null;
  reviewedSigned: boolean;
  reviewedDate: string | null;
  approvedUser: CompanyDocumentUser | null;
  approvedSigned: boolean;
  approvedDate: string | null;
  attachments: CompanyDocumentAttachment[];
  createTime?: string;
  updateTime?: string;
}

export interface CompanyDocumentGroupPayload {
  code: string;
  name: string;
}

export interface CompanyDocumentPayload {
  department: string;
  name: string;
  companyDocumentGroupId: number | null;
}

export interface CompanyDocumentRevisionPayload {
  companyDocumentId: number;
  date: string;
  description: string;
  reviewedUserId: number;
  approvedUserId: number;
}

export const COMPANY_DOCUMENT_REVISION_STATUS_LABELS: Record<CompanyDocumentRevisionStatus, string> = {
  IN_USE: 'In use',
  IN_PROGRESS: 'In progress',
  OBSOLETE: 'Obsolete'
};

/** Classe del badge di stato: verde in uso, arancio in lavorazione, rosso obsoleta. */
export const COMPANY_DOCUMENT_REVISION_STATUS_BADGES: Record<CompanyDocumentRevisionStatus, string> = {
  IN_USE: 'bg-success',
  IN_PROGRESS: 'bg-warning',
  OBSOLETE: 'bg-danger'
};

export const COMPANY_DOCUMENT_ERROR_MESSAGES: Record<string, string> = {
  COMPANY_DOCUMENT_GROUP_NOT_FOUND: 'Company document group not found.',
  COMPANY_DOCUMENT_NOT_FOUND: 'Company document not found.',
  COMPANY_DOCUMENT_REVISION_NOT_FOUND: 'Company document revision not found.',
  COMPANY_DOCUMENT_REVISION_ATTACHMENT_REQUIRED: 'Upload an attachment before requesting the confirmation.',
  COMPANY_DOCUMENT_REVISION_SIGN_NOT_ALLOWED: 'You are not allowed to sign this revision, or you have already signed it.',
  COMPANY_DOCUMENT_REVISION_MOVE_IN_USE_FORBIDDEN: 'Only a Quality Manager can move a revision in use.',
  COMPANY_DOCUMENT_REVISION_MOVE_IN_USE_NOT_ALLOWED: 'The revision must be fully signed and be the latest one to be moved in use.'
};

export function companyDocumentRevisionInProgressId(companyDocument?: CompanyDocument | null): number | null {
  const revisionId = companyDocument?.revisionInProgress?.companyDocumentRevisionId;
  return revisionId != null ? Number(revisionId) : null;
}

export function companyDocumentUserFullName(user?: CompanyDocumentUser | null): string {
  if (!user) {
    return '';
  }
  return `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim();
}
